import { EventType } from "@/analytics/analytics-events";
import { OrthophotoGenerationEventProperties } from "@/analytics/orthophoto-generation-event-properties";
import { useCurrentScene } from "@/modes/mode-data-context";
import { selectSelectedOrthophotoSide } from "@/store/modes/export-mode-selectors";
import { setSelectedOrthophotoSide } from "@/store/modes/export-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { useBoxControlsClippingPlanes } from "@/utils/box-controls-context";
import { useLocalStorage } from "@faro-lotv/app-component-toolbox";
import {
  Alert,
  Dropdown,
  FaroButton,
  FaroText,
  TextField,
  TranslateVar,
  ViewPredefinedBackIcon,
  ViewPredefinedBottomIcon,
  ViewPredefinedFrontIcon,
  ViewPredefinedLeftIcon,
  ViewPredefinedRightIcon,
  ViewPredefinedTopIcon,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { isValidFilename, VALID_CHARACTERS } from "@faro-lotv/foundation";
import { assert, BoxGizmoSide } from "@faro-lotv/lotv";
import { selectDatasetOrArea, selectIElement } from "@faro-lotv/project-source";
import { SvgIconProps } from "@mui/material";
import { Stack } from "@mui/system";
import { useCallback, useState, type ElementType, type JSX } from "react";
import { OrthophotoPreview } from "./orthophoto-preview";
import {
  isTooLargeForBrowsers,
  OrthophotoResolutionInput,
  RESOLUTION_TYPES,
  ResolutionType,
  validateResolution,
  ViewportSize,
} from "./orthophoto-resolution-input";
import { useOrthophotoBackend } from "./use-export-orthophoto";

function getPlaneLabel(
  label: string,
  Icon: ElementType<SvgIconProps>,
): JSX.Element {
  return (
    <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
      <Icon />
      <FaroText variant="bodyM">{label}</FaroText>
    </Stack>
  );
}

function isBoxGizmoSide(value: string): value is BoxGizmoSide {
  return PLANE_DEFINITIONS.some((def) => def.value === value);
}

/**
 * @param fileName the file name to validate
 * @returns the error text for an invalid file name, or undefined if the name is valid
 */
function computeFileNameErrorText(fileName: string): string | undefined {
  if (fileName.length === 0) {
    return "File name cannot be empty.";
  }
  if (!isValidFilename(fileName)) {
    return `The filename contains invalid characters. Allowed characters are: ${VALID_CHARACTERS}.`;
  }
}

type PlaneKey = "top" | "bottom" | "front" | "back" | "left" | "right";

interface PlaneDefinition {
  /** Unique string identifier for the plane */
  key: PlaneKey;
  /** Human-readable label for the plane */
  label: JSX.Element;
  /** Corresponding BoxGizmoSide enum value */
  value: BoxGizmoSide;
  /**
   * Backend API index for this plane
   * The backend expects RHZUP coordinate system
   *
   * @see https://dev.azure.com/faro-connect/GeometryServices/_git/Backends?path=/tools/pc-converter/src/cli_args.rs&version=GBmain&line=124&lineEnd=130&lineStartColumn=1&lineEndColumn=72&lineStyle=plain&_a=contents
   */
  backendPlaneIndex: number;
}

const PLANE_DEFINITIONS: PlaneDefinition[] = [
  {
    key: "top",
    value: BoxGizmoSide.zPositive,
    label: getPlaneLabel("Top", ViewPredefinedTopIcon),
    backendPlaneIndex: 4,
  },
  {
    key: "bottom",
    value: BoxGizmoSide.zNegative,
    label: getPlaneLabel("Bottom", ViewPredefinedBottomIcon),
    backendPlaneIndex: 5,
  },
  {
    key: "right",
    value: BoxGizmoSide.xPositive,
    label: getPlaneLabel("Right", ViewPredefinedRightIcon),
    backendPlaneIndex: 0,
  },
  {
    key: "left",
    value: BoxGizmoSide.xNegative,
    label: getPlaneLabel("Left", ViewPredefinedLeftIcon),
    backendPlaneIndex: 1,
  },
  {
    key: "front",
    value: BoxGizmoSide.yNegative,
    label: getPlaneLabel("Front", ViewPredefinedFrontIcon),
    backendPlaneIndex: 2,
  },
  {
    key: "back",
    value: BoxGizmoSide.yPositive,
    label: getPlaneLabel("Back", ViewPredefinedBackIcon),
    backendPlaneIndex: 3,
  },
] as const;

/** @returns the form to export an orthophoto from */
export function OrthophotoExportForm(): JSX.Element {
  const { main } = useCurrentScene();
  const { orthophotoLimits, generateOrthophoto } = useOrthophotoBackend();

  const mainElement = useAppSelector(selectIElement(main?.id ?? ""));
  assert(mainElement, "Main element must be defined in the current scene");
  const dataset = useAppSelector(selectDatasetOrArea(mainElement));

  const [fileName, setFileName] = useState(dataset?.name ?? "Orthophoto");

  const [resolutionLocalStorage, setResolutionLocalStorage] = useLocalStorage(
    "orthophotoResolution",
    {
      resolutionType: RESOLUTION_TYPES.millimetersPerPixel.value,
      resolutionValue: RESOLUTION_TYPES.millimetersPerPixel.default,
    },
  );

  const [resolutionType, setResolutionType] = useState<ResolutionType>(
    resolutionLocalStorage.resolutionType,
  );
  const [resolution, setResolution] = useState(
    resolutionLocalStorage.resolutionValue,
  );
  const [imageDimensions, setImageDimensions] = useState<ViewportSize>();

  const [fileNameErrorText, setFileNameErrorText] = useState(() =>
    computeFileNameErrorText(fileName),
  );
  const [resolutionErrorText, setResolutionErrorText] = useState<string>();

  const [hasEditedName, setHasEditedName] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const selectedPlane = useAppSelector(selectSelectedOrthophotoSide);

  const dispatch = useAppDispatch();

  const onFileNameChange = useCallback(
    (newFileName: string) => {
      setFileName(newFileName);

      if (!hasEditedName) setHasEditedName(true);

      setFileNameErrorText(computeFileNameErrorText(newFileName));
    },
    [hasEditedName],
  );

  const onCameraResize = useCallback(
    ({ width, height }: ViewportSize) => {
      try {
        const imageDimensions = validateResolution(
          resolutionType,
          resolution,
          orthophotoLimits,
          width,
          height,
        );

        if (imageDimensions) {
          setImageDimensions(imageDimensions);
        }

        // Clear any existing error if validation passes
        setResolutionErrorText(undefined);
      } catch (error) {
        setResolutionErrorText(
          error instanceof Error ? error.message : "Invalid resolution",
        );
      }
    },
    [orthophotoLimits, resolution, resolutionType],
  );

  const onPlaneChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newPlane = e.target.value;
      assert(isBoxGizmoSide(newPlane), `Invalid BoxGizmoSide: ${newPlane}`);
      dispatch(setSelectedOrthophotoSide(newPlane));
    },
    [dispatch],
  );

  const onExportOrthophoto = useCallback(async () => {
    setHasSubmitted(true);

    // Save resolution to local storage for next time
    setResolutionLocalStorage({
      resolutionType,
      resolutionValue: resolution,
    });

    // Convert pixels per meter back to meters per pixel for backend
    const metersPerPixel =
      resolutionType === RESOLUTION_TYPES.pixelsPerMeter.value
        ? 1 / resolution
        : resolution / 1000;

    // Find the plane definition corresponding to the selected BoxGizmoSide
    const planeDefinition = PLANE_DEFINITIONS.find(
      (def) => def.value === selectedPlane,
    );
    assert(planeDefinition, "Selected plane is invalid");

    Analytics.track<OrthophotoGenerationEventProperties>(
      EventType.generateOrthophoto,
      {
        hasEditedName,
        resolutionType,
        resolution,
        plane: planeDefinition.key,
        size: imageDimensions,
      },
    );

    await generateOrthophoto(
      fileName,
      metersPerPixel,
      planeDefinition.backendPlaneIndex,
    );

    // Reset the submission state to allow re-submission
    setHasSubmitted(false);
  }, [
    setResolutionLocalStorage,
    resolutionType,
    resolution,
    hasEditedName,
    imageDimensions,
    generateOrthophoto,
    fileName,
    selectedPlane,
  ]);

  const clippingPlanesBox = useBoxControlsClippingPlanes();

  return (
    <Stack sx={{ justifyContent: "space-between", height: "100%" }}>
      <Stack sx={{ gap: 2 }}>
        <FaroText variant="helpText">
          For faster experience, consider exporting smaller sections.
        </FaroText>
        <TextField
          label="File name"
          fullWidth
          placeholder="Enter file name"
          text={fileName}
          onTextChanged={onFileNameChange}
          error={fileNameErrorText}
        />
        <OrthophotoResolutionInput
          resolutionType={resolutionType}
          setResolutionType={setResolutionType}
          resolution={resolution}
          setResolution={setResolution}
          resolutionErrorText={resolutionErrorText}
          setResolutionErrorText={setResolutionErrorText}
        />
        <Dropdown
          label="Select plane"
          value={selectedPlane}
          options={PLANE_DEFINITIONS}
          shouldCapitalize={false}
          onChange={onPlaneChange}
          fullWidth
          helpText="Select the reference plane from the dropdown or click a face on the clipping box in the 3D view"
        />
        <FaroText variant="labelM">Orthophoto preview</FaroText>
        <OrthophotoPreview onCameraResize={onCameraResize} />
        {imageDimensions && (
          <FaroText variant="helpText">
            Dimensions:{" "}
            <TranslateVar name="dimensions">
              {imageDimensions.width}x{imageDimensions.height}{" "}
            </TranslateVar>
            pixels
          </FaroText>
        )}
      </Stack>

      <Stack sx={{ gap: 2 }}>
        {!clippingPlanesBox && (
          <Alert variant="warning" title="No data selected">
            Adjust the clipping box so it includes geometry. The export will be
            enabled once data is inside the section
          </Alert>
        )}
        {imageDimensions &&
          !resolutionErrorText &&
          isTooLargeForBrowsers(imageDimensions) && (
            <Alert variant="warning" title="Very large orthophoto">
              The resulting image may be too large to be viewed in a web
              browser. If that happens, use a desktop image processing or GIS
              application to open the file.
            </Alert>
          )}
        <FaroButton
          onClick={onExportOrthophoto}
          aria-label="start orthophoto export"
          disabled={
            !!(fileNameErrorText ?? resolutionErrorText) || !clippingPlanesBox
          }
          isLoading={hasSubmitted}
        >
          Export
        </FaroButton>
      </Stack>
    </Stack>
  );
}
